import { useParams } from "react-router-dom";
import { DiaEditorSaveArgs, useDiaEditor } from "../../components/DiaEditor";
import {
    Campaign, CreateCampaignInput, CreateCampaignMutationMutation,
    GetCampaignQueryQueryResult, UpdateCampaignInput, UpdateCampaignMutationMutation,
    UpdatePanelBookingInput,
    useCreateCampaignMutationMutation,
    useGetCampaignQueryQuery,
    useUpdateCampaignMutationMutation
} from "../../generated/graphql";
import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { useUserContext } from "../../contexts/user-context";
import { NonIdealState } from "../../components/NonIdealState";
import { CampaignForm } from "./form";
import WaitSpinner from "../../components/wait-spinner";
import * as yup from "yup";
import { campaignFieldsToTabs } from "./fieldsToTabs";
import { useDaybookContext } from "../../contexts/Daybook/DayBookContext";
import { CampaignContextProvider } from "../../contexts/campaign-context";
import { formatISO } from "date-fns";



const isoDateReg = /\d{4}-[01]\d-[0-3]\d/;


type CampaignTransient = {
    auto_upgrade: boolean,
    tempFiles: any[]
};


const validationSchema = yup.object().shape({
    name: yup.string().min(3),
    start_date: yup.string().matches(isoDateReg).required(),
    completion_date: yup.string().matches(isoDateReg).required(),
    clientCompanyContact: yup.object().required(),
    clientCompany: yup.object().required(),
    clientSubType: yup.object().required(),
    currency: yup.object().required(),
    salesTeam: yup.object().required(),
    sub_teams_scotland: yup.string()
        .nullable()
        .when('salesTeam', {
            is: (salesTeam) => salesTeam?.prefix === 'BHS', // Only validate if salesTeam.prefix is 'BHS'
            then: yup.string().required(),
            otherwise: yup.string().nullable(),
        }),
    ourContact: yup.object().required(),
    areaBookings: yup.array().of(yup.object().shape({
        regionType: yup.object().required(),
        posterSize: yup.object().required(),
        areaBookingItems: yup.array().of(yup.object().shape({
            campaignComponent: yup.object().shape({
                region: yup.object().required()
            })
            //            distributor: yup.object().required()
        }))
    })),
    experientials: yup.array().of(yup.object().shape({
        campaignComponent: yup.object().shape({
            region: yup.object().required(),
            regionType: yup.object().required(),
            unit_value_local: yup.number().min(1),
            qty: yup.number().min(1)
        }),
        experientialType: yup.object().required(),
        start_date: yup.string().min(10),
        end_date: yup.string().min(10),
    })),
    ambients: yup.array().of(yup.object().shape({
        campaignComponent: yup.object().shape({
            region: yup.object().required(),
            regionType: yup.object().required(),
            unit_value_local: yup.number().min(1),
            qty: yup.number().min(1)
        }),
        ambientType: yup.object().required(),
        start_date: yup.string().min(10),
        end_date: yup.string().min(10),
    })),
    mediaProductions: yup.array().of(yup.object().shape({
        campaignComponent: yup.object().shape({
            unit_value_local: yup.number(),
            qty: yup.number().min(1)
        }),
        size: yup.object().required(),
        mediaType: yup.object().required()
    })),
    creativeStudio: yup.array().of(yup.object().shape({
        campaignComponent: yup.object().shape({
            unit_value_local: yup.number().min(1),
            qty: yup.number().min(1)
        }),
        creativeStudioType: yup.object().required(),
        creativeStudioTypeDetail: yup.object().required(),
        start_date: yup.string().min(10),
        end_date: yup.string().min(10)
    })),
    sundries: yup.array().of(yup.object().shape({
        name: yup.string().required(),
        sundryType: yup.object().required()
    })),
    costs: yup.array().of(yup.object().shape({
        invoice_number: yup.string().required(),
        costSupplier: yup.object().required()
    }))
});

const campaignRelationships = {
    'costs': 'create',
    'costs.currency': 'connect',
    'costs.costSupplier': 'connect',
    areaBookings: 'create',
    'regionalAmAllocation': 'connect',
    'areaBookings.areaBookingItems.campaignComponent.region': 'connect',
    'areaBookings.areaBookingItems.campaignComponent.currency': 'connect',
    'areaBookings.posterSize': 'connect',
    'areaBookings.regionType': 'connect',
    'areaBookings.currency': 'connect',
    'areaBookings.areaBookingItems': 'create',
    'areaBookings.areaBookingItems.distributor': 'connect',
    'clientCompany': 'connect',
    'clientCompanyContact': 'connect',
    'clientSubType': 'connect',
    'currency': 'connect',
    'currencies': 'connect',
    'mediaProductions': 'create',
    'mediaProductions.mediaType': 'connect',
    'mediaProductions.size': 'connect',
    'mediaProductions.campaignComponent.currency': 'connect',
    'creativeStudio': 'create',
    'creativeStudio.creativeStudioType': 'connect',
    'creativeStudio.campaignComponent.region': 'connect',
    'creativeStudio.campaignComponent.regionType': 'connect',
    'creativeStudio.creativeStudioTypeDetail': 'connect',
    'creativeStudio.campaignComponent.currency': 'connect',
    'experientials.experientialType': 'connect',
    'experientials.campaignComponent.region': 'connect',
    'experientials.campaignComponent.regionType': 'connect',
    'experientials.campaignComponent.currency': 'connect',
    'ambients.ambientType': 'connect',
    'ambients.campaignComponent.region': 'connect',
    'ambients.campaignComponent.currency': 'connect',
    'sundries.sundryType': 'connect',
    'sundries.campaignComponent.regionType': 'connect',
    'sundries.campaignComponent.region': 'connect',
    'sundries.campaignComponent.currency': 'connect',
    'ourContact': 'connect',
    'panelBookings': 'create', // trash
    'salesTeam': 'connect',
    'previewTags': 'connect',
    'paymentTerms': 'connect',
    'artworkFiles.distributor': 'connect'
};

function cleanGraphqlVariables(variables: UpdateCampaignInput, transientValues: CampaignTransient) {
    console.log('preupdate', variables, transientValues);
    const sections = ['mediaProductions', 'experientials', 'ambients', 'areaBookings', 'panelBookings', 'costs', 'sundries', 'creativeStudio'];
    if (!variables) {
        alert('NO VARIABLES');
        return;
    }
    sections.forEach((key) => {
        if (!variables[key]) {
            variables[key] = {};
        }
    });
    if (variables.panelBookings?.delete) {
        delete variables.panelBookings.delete;
    }
    if (variables.panelBookings?.create) {
        delete variables.panelBookings.create;
    }
    const newPb: UpdatePanelBookingInput[] = [];
    if (variables.panelBookings?.update) {
        variables.panelBookings.update.forEach((v) => {
            if (v.instructions !== undefined || v.is_received !== undefined || v.is_photo_paid !== undefined) {
                const obj: UpdatePanelBookingInput = { id: v.id };
                if (v.instructions !== undefined) {
                    obj.instructions = v.instructions;
                }
                if (v.is_received !== undefined) {
                    obj.is_received = v.is_received;
                }
                if (v.is_photo_paid !== undefined) {
                    obj.is_photo_paid = v.is_photo_paid;
                }
                newPb.push(obj as UpdatePanelBookingInput);
            }
        });
        variables.panelBookings.update = newPb;
    }
    delete variables?.outboundChallenges;
    delete variables?.inboundChallenges;
    delete variables?.components;
    delete variables.totalCost;
    delete variables.discountableTotal;
    delete variables.undiscountableTotal;
    delete variables?.tempFiles;
    delete variables?.__cloned;
    delete variables?.job_reference;
    delete variables?.campaignExchangeRates;
    delete variables?.created_at;

    if (variables?.areaBookings?.create) {
        variables.areaBookings.create.forEach((booking) => {
            delete booking.total_value;
            delete booking.num_posters;
            delete booking.first_up;
            delete booking.last_down;
            if (booking.areaBookingItems?.create) {
                booking.areaBookingItems.create.forEach((item) => {
                    delete item.created_at;
                });
            }
        });
    }

    if (variables.digitalBookings) {
        if (variables.digitalBookings.update) {
            variables.digitalBookings.update = variables.digitalBookings.update.map((booking) => {
                const cleanedBooking = {
                    id: booking.id,
                };
                if (booking.is_photo_paid !== undefined) {
                    cleanedBooking.is_photo_paid = booking.is_photo_paid;
                }
                return cleanedBooking;
            });
        }

        if (variables.digitalBookings.create) {
            delete variables.digitalBookings.create;
        }
        if (variables.digitalBookings.delete) {
            delete variables.digitalBookings.delete;
        }
    }


    if (variables.id != 'new') {
        variables.auto_upgrade = transientValues.auto_upgrade;
    }
    return variables;
}

export function CampaignEditor(): ReactElement {
    const params = useParams();
    const [errorsToTabs, setErrorsToTabs] = useState({});
    const isNew = (params.id == 'new');
    const daybook = useDaybookContext();
    const userContext = useUserContext()
    const user = userContext?.user;
    const query = useGetCampaignQueryQuery({ variables: { ref: params.id, regionAreaBookingPricing: !!daybook?.regionAreaBookingPricing }, skip: isNew || !daybook });
    const [saveMutation, saveResult] = useUpdateCampaignMutationMutation();
    const [createMutation, createResult] = useCreateCampaignMutationMutation();
    const save = useCallback(({ values }: DiaEditorSaveArgs<Campaign>) => {
        return saveMutation({ variables: { input: values as UpdateCampaignInput, regionAreaBookingPricing: !!daybook?.regionAreaBookingPricing } });
    }, [daybook, saveMutation]);
    const create = useCallback(({ values }: { values: Campaign }) => {
        return createMutation({ variables: { input: values as CreateCampaignInput, regionAreaBookingPricing: !!daybook?.regionAreaBookingPricing } });
    }, [daybook, createMutation]);
    const getValue = useCallback((result) => {
        return result.data?.campaign ?? null;
    }, []);
    const getSaveValue = useCallback((result) => {
        return result.data?.updateCampaign;
    }, []);
    /*const getCreateValue = useCallback((result)=>{
        return result.data?.createCampaign;
    },[]);*/
    // sales team is the only sales team, otherwise null
    const getCreateRedirect = useCallback(({ data, baseUrl }) => {
        console.log('get redirect from', data, baseUrl + data?.createCampaign?.job_reference);
        userContext.addToShortcuts(data.data.createCampaign.id);
        return baseUrl + data?.data.createCampaign?.job_reference;
    }, [user]);
    const makeBlankValues = useCallback((): Campaign => {
        const campaign: Campaign = {
            id: 'new',
            name: '',
            invoice: '',
            purchase_order: '',
            completion_date: '',
            start_date: '',
            job_reference: '',
            status: 'Draft',
            notes: '',
            salesTeam: null,
            sub_teams_scotland: '',
            discount_percentage: 0,
            discount_final: 0,
            regionalAmAllocation: null,
            posters_received: 'NONE',
            currency: null,
            clientSubType: null,
            clientType: null,
            is_purchase_order_required: true,
            clientCompany: null,
            clientCompanyContact: null,
            ourContact: null,
            ambients: [],
            experientials: [],
            costs: [],
            sundries: [],
            areaBookings: [],
            panelBookings: [],
            mediaProductions: [],
            creativeStudio: [],
            authorizationFiles: [],
            artworkFiles: [],
            invoiceFiles: [],
            media_production_notes: ''
        };
        const st = user?.salesTeams?.length == 1 ? user.salesTeams[0] : null;
        if (st) {
            campaign.salesTeam = {
                id: st.id, name: st.name
            };
            campaign.currency = {
                id: st?.defaultCurrency?.id as string,
                label: st?.defaultCurrency?.label,
                glyph: st?.defaultCurrency?.glyph
            };
        }
        campaign.ourContact = {
            id: user?.id as string,
            name: user?.name,
            role: user?.role
        };
        return campaign;
    }, [user]);
    function cleanTransients({ setTransient }) {
        // runs after a successful save to clear this flag
        setTransient('auto_upgrade', false);
    }
    const data = useDiaEditor<Campaign, GetCampaignQueryQueryResult, CreateCampaignMutationMutation, UpdateCampaignMutationMutation, CampaignTransient>
        ({
            save,
            saveResult,
            create,
            createResult: createResult,
            query: query,
            relationships: campaignRelationships,
            baseUrl: '/campaigns/',
            blankValues: makeBlankValues,
            label: 'Campaign',
            permission: 'campaign',
            afterSave: cleanTransients,
            transientValues: {
                auto_upgrade: false,
                tempFiles: []
            },
            validationSchema: validationSchema,
            getValue: getValue,
            getSaveValue: getSaveValue,
            getCreateRedirect: getCreateRedirect,
            allowRedirectWhen: (newPath, ref) => {
                console.log('check', newPath, ref, newPath.startsWith('/campaigns/' + ref));
                return (newPath.startsWith('/campaigns/' + ref));
            },
            cleanGraphqlVariables
        });
    console.log('EDITOR RENDER', data.form?.values?.currencies, query?.data);
    // parse errors
    useEffect(() => {
        if (data?.form?.values?.id) {
            data.form.setTransient('auto_upgrade', false);
        }
    }, [data?.form?.values?.id]);

    useEffect(() => {
        if (!data.form.errors) {
            if (errorsToTabs && Object.keys(errorsToTabs).length) {
                setErrorsToTabs(null);
                return;
            }
            return;
        }
        setErrorsToTabs(campaignFieldsToTabs(data.form.errors));
    }, [data.form.errors]);

    if (query.error) {
        return <NonIdealState
            severity="error"
            title="An error has occurred"
            message="GraphQL error encountered"
        />;
    }
    if (data.form.status == 'loading' || !daybook) {
        console.log('LOADING REASON', data.form.status, daybook);
        return <WaitSpinner variant="panel" />;
    }
    console.log('LOADING', query.loading, query.data, data.form.values);
    const dt = formatISO(new Date(), { representation: 'date' })
    return <>{data.confirmation}<CampaignContextProvider campaign_start_date={data.form.values.start_date} campaign_created_at={data.form.values.created_at ?? dt}><CampaignForm diaForm={data.form} tabErrors={errorsToTabs} /></CampaignContextProvider></>;
}